import React from 'react';

import { ToggleButton } from '@change/ds-core-ui';
import { PauseCircleFilledIcon, PlayCircleFilledIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { LikeVideoButton, MoreActionsVideoMenu, VideoPlayerSunrise } from 'src/app/shared/components/petitionVideo';

import type { SupporterVideo } from '../../../../types';

import { useVideoComponent } from './hooks/useVideoComponent';

type VideoComponentProps = {
	video: SupporterVideo;
	currentlyPlaying: string;
	setCurrentlyPlaying: (videoUrl: string) => void;
	removeVideoFromState: (videoId?: string) => void;
	className?: string;
};

/**
 * Renders the video player in an inline block. Includes:
 *  - Fixed-height video container
 *  - Play/pause icon over the video
 *  - Like, delete, and report buttons beneath the video
 */
export function VideoComponent({
	className,
	video,
	currentlyPlaying,
	setCurrentlyPlaying,
	removeVideoFromState,
}: VideoComponentProps): React.JSX.Element | null {
	const { id: videoId, videoUrl, user } = video;

	const {
		data: { isPlaying, videoRef, petitionId, petitionSlug },
		actions: { toggleVideoPlayback, trackVideoError, setIsPlaying },
	} = useVideoComponent({ video, currentlyPlaying, setCurrentlyPlaying });

	return (
		<div
			key={videoId}
			className="flex min-h-auto min-w-auto flex-col"
			onFocus={(event) => {
				event.target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
			}}
			data-testid={`video-${videoId}-container`}
		>
			<div className={cn('relative flex', className)} data-testid={`video-${videoId}-with-icon`}>
				<div className="absolute right-4 bottom-4 left-4 z-20 inline-flex items-center">
					<div className="flex-1">
						<LikeVideoButton video={video} petitionId={petitionId} renderedOn="petition_page" />
					</div>
					<div className="flex flex-1 justify-center">
						<MoreActionsVideoMenu
							video={video}
							removeVideoFromState={removeVideoFromState}
							petitionId={petitionId}
							petitionSlug={petitionSlug}
						/>
					</div>
					<div className="flex flex-1 justify-end">
						<ToggleButton
							aria-label="Play or pause video"
							isSelected={isPlaying}
							onChange={toggleVideoPlayback}
							pinnedIcon={<PauseCircleFilledIcon size="large" className="text-[#FFDB00]" />}
							unpinnedIcon={<PlayCircleFilledIcon size="large" className="text-[#FFDB00]" />}
						/>
					</div>
				</div>

				<VideoPlayerSunrise
					creator={user?.shortDisplayName}
					ref={videoRef}
					tabIndex={0}
					videoUrl={videoUrl}
					videoId={videoId}
					onClick={() => {
						void toggleVideoPlayback();
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							void toggleVideoPlayback();
						}
					}}
					onPause={(_) => {
						setIsPlaying(false);
					}}
					onError={() => {
						// Unfortunately the event passed to this handler doesn't provide any error details.
						trackVideoError(new Error('Video element error'));
					}}
				/>
			</div>
		</div>
	);
}
